/**
 * usePaymentFlow: the card payment's lifecycle and its recovery, one controller for the Mini App's
 * payment page and the studio website's payment step (shared-client-screens D4; rows P4, P5, P7).
 *
 * Phases: form (filling in the card), windowTooClose (too close to the start to pay: Pick a new
 * time), benignTerminal (an earlier payment already went through: "You're all set"), confirming
 * (paid, the booking being confirmed) and slowConfirm (still confirming after ten seconds).
 *
 * It owns what both surfaces did twice: the card panel's reports (complete, in flight, decline,
 * the intent's expiry), the one conflict path (the last known payment, else the reservation-bound
 * sessionStorage carrier, else the benign terminal), the status read while confirming, the
 * slow-confirm timer, the hold timer (B11), and the poll's endings (confirmed, lapsed, stale).
 * Each surface passes its transport (intent request, status read, flow re-read) and what to do
 * once the booking is confirmed; the app keeps Venmo, the website offers card only (row P1).
 * Draws nothing and navigates nowhere.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type RefObject } from 'react';
import { carrierKey, type CardCheckoutPanelHandle, type CardDecline } from '@/components/payment/CardCheckoutPanel';
import type { PaymentStatusResponse } from '@/api/types';
import type { CheckoutError } from '@/hooks/checkoutError';
import type { PaymentIntentRequest } from '@/hooks/useCreatePaymentIntent';
import type { CheckoutEntry } from '@/types/checkout';
import { settleStripeReturn, stripeReturnPayment, useStripeReturnPayment } from '@/lib/stripeReturn';

/** Success beat: the confirming overlay stays this long after CONFIRMED before the booking opens. */
export const SUCCESS_DISPLAY_MS = 500;

/** B8: time in the confirming state before the slow-confirm overlay. */
export const SLOW_CONFIRM_MS = 10_000;

export type PaymentFlowPhase = 'form' | 'windowTooClose' | 'benignTerminal' | 'confirming' | 'slowConfirm';

/** The payment status read while confirming. */
export interface PaymentStatusRead {
  readonly data?: PaymentStatusResponse;
  readonly error?: CheckoutError | null;
}

/** How each surface reaches the server. */
export interface PaymentFlowTransport {
  /** Creates the card payment (the app's client, or the website session's). */
  readonly intentRequest: PaymentIntentRequest;
  /** The payment status read, a hook: called on every render, reading only while `enabled`. */
  readonly useStatus: (entry: CheckoutEntry, intentId: string | null, options: { enabled: boolean }) => PaymentStatusRead;
  /** Re-reads the flow, so the payment step follows the server (card paused, the hold gone). */
  readonly refetchFlow: () => void;
}

export interface UsePaymentFlowInput {
  readonly entry: CheckoutEntry;
  readonly transport: PaymentFlowTransport;
  /** The held reservation, to trust only a stored payment made for it. */
  readonly reservation?: { readonly eventHoldId?: string | null; readonly placeholderId?: string | null };
  /** When the held time runs out, until the card payment names its own expiry (B11). */
  readonly holdExpiresAt?: string | null;
  /** The booking was confirmed: at once, before the success beat (the app updates its cache here). */
  readonly onConfirmedNow?: (bookingId: string | undefined) => void;
  /** The booking was confirmed, after the success beat: open it. */
  readonly onBooked: (bookingId: string | undefined) => void;
}

export interface PaymentFlow {
  readonly phase: PaymentFlowPhase;
  /** confirming or slowConfirm: the overlay is up and nothing should move the page. */
  readonly confirming: boolean;
  /** The card form is filled in enough to pay. */
  readonly complete: boolean;
  /** A card payment is being sent. */
  readonly submitting: boolean;
  readonly decline: CardDecline | null;
  /** The held time ran out on the client's clock: Pay is off while the flow is read again. */
  readonly timerExpired: boolean;
  /** The server refused the card payment as paused (CARD_UNAVAILABLE): Pay now, with no flow to re-read, shows the hard block. */
  readonly cardRefused: boolean;
  /** When the hold runs out: the card payment's own expiry once it has one. */
  readonly expiresAt: string | null;
  /** Pay with the card as filled in. */
  readonly pay: () => void;
  /** A payment already made: read it back if one is known, else "You're all set". */
  readonly conflict: () => void;
  /** Read back a known payment; false when none is known. */
  readonly resumeKnownPayment: () => boolean;
  /** Back from a Stripe hand-off, its payment is being read: no card form may start meanwhile (a new one would replace it). */
  readonly resuming: boolean;
  /** The card panel's props. */
  readonly panel: {
    readonly key: number;
    readonly ref: RefObject<CardCheckoutPanelHandle>;
    readonly entry: CheckoutEntry;
    readonly intentRequest: PaymentIntentRequest;
    readonly onCompleteChange: (complete: boolean) => void;
    readonly onIntentReady: (expiresAt: string | null) => void;
    readonly onSubmitInFlightChange: (inFlight: boolean) => void;
    readonly onPaymentSubmitted: (payload: { stripePaymentIntentId: string }) => void;
    readonly onConflict: () => void;
    readonly onDecline: (decline: CardDecline | null) => void;
    readonly onError: (error: CheckoutError) => void;
  };
}

/** The later of two deadlines (either may be missing). */
function laterOf(a: string | null, b: string | null): string | null {
  if (!a || !b) return a ?? b;
  return Date.parse(a) >= Date.parse(b) ? a : b;
}

/**
 * The payment this checkout came back from Stripe with: only one its own carrier names, so a return
 * meant for another checkout never takes this one over (stripeReturn.ts; Codex re-check 2026-10-26).
 */
function returnFor(entry: CheckoutEntry): string | null {
  const returning = stripeReturnPayment();
  if (!returning) return null;
  try {
    const carrier = JSON.parse(sessionStorage.getItem(carrierKey(entry)) ?? 'null') as { stripePaymentIntentId?: unknown } | null;
    return carrier?.stripePaymentIntentId === returning ? returning : null;
  } catch {
    return null;
  }
}

export function usePaymentFlow({ entry, transport, reservation, holdExpiresAt, onConfirmedNow, onBooked }: UsePaymentFlowInput): PaymentFlow {
  // Back from a Stripe hand-off (3D Secure, a wallet): the payment it went to make is read first, and no new
  // card form starts meanwhile, so a payment that went through while the client was away opens its booking.
  const [resumed] = useState(() => returnFor(entry));
  const [phase, setPhase] = useState<PaymentFlowPhase>(resumed ? 'confirming' : 'form');
  const [complete, setComplete] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [decline, setDecline] = useState<CardDecline | null>(null);
  const [intentExpiresAt, setIntentExpiresAt] = useState<string | null>(null);
  const [timerExpired, setTimerExpired] = useState(false);
  const [cardRefused, setCardRefused] = useState(false);
  // The current or last known payment, read while confirming (the echo rule).
  const [intentId, setIntentId] = useState<string | null>(resumed);
  const returning = useStripeReturnPayment();
  const resuming = resumed !== null && returning === resumed;
  // A return this checkout does not own is let go, so the held-time prompts are not held back for it.
  useEffect(() => {
    if (!resumed) settleStripeReturn();
  }, [resumed]);
  // A booked return settles once this checkout is gone: settled while it is still on screen, the held time that
  // just became the booking would read as run out for a moment. Only after a booking, so a development
  // remount does not settle it.
  const booked = useRef(false);
  useEffect(
    () => () => {
      if (booked.current) settleStripeReturn();
    },
    [],
  );
  // Bumped to start the card panel over for a fresh attempt (STALE_CHECKOUT).
  const [attempt, setAttempt] = useState(0);
  const ref = useRef<CardCheckoutPanelHandle>(null);
  const { refetchFlow } = transport;

  const confirming = phase === 'confirming' || phase === 'slowConfirm';
  const status = transport.useStatus(entry, intentId, { enabled: confirming });

  // The endings run the latest handlers without re-running on a new handler.
  const handlers = useRef({ onConfirmedNow, onBooked });
  handlers.current = { onConfirmedNow, onBooked };

  // B11: once the card payment exists, its expiry is the timer's truth; at zero Pay goes off and the flow is read again.
  // A hold only ever lengthens (Keep, max-never-shorten), so after a Keep the flow's later deadline wins (Codex review 2026-10-26).
  const expiresAt = laterOf(intentExpiresAt, holdExpiresAt ?? null);
  useEffect(() => {
    setTimerExpired(false);
    if (!expiresAt) return;
    const ms = new Date(expiresAt).getTime() - Date.now();
    if (ms <= 0) {
      setTimerExpired(true);
      refetchFlow();
      return;
    }
    const timer = setTimeout(() => {
      setTimerExpired(true);
      refetchFlow();
    }, ms);
    return () => clearTimeout(timer);
  }, [expiresAt, refetchFlow]);

  // The carrier (Decision 2): the panel writes it; this reads it, trusting only one made for this reservation.
  const purgeCarrier = useCallback(() => {
    try {
      sessionStorage.removeItem(carrierKey(entry));
    } catch {
      /* sessionStorage unavailable: nothing to purge */
    }
  }, [entry]);
  const eventHoldId = reservation?.eventHoldId ?? null;
  const placeholderId = reservation?.placeholderId ?? null;
  const readCarrierId = useCallback((): string | null => {
    let raw: string | null = null;
    try {
      raw = sessionStorage.getItem(carrierKey(entry));
    } catch {
      return null;
    }
    if (!raw) return null;
    let parsed: unknown;
    try {
      parsed = JSON.parse(raw);
    } catch {
      purgeCarrier();
      return null;
    }
    const triple = parsed && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : null;
    if (
      !triple ||
      typeof triple.stripePaymentIntentId !== 'string' ||
      typeof triple.reservationId !== 'string' ||
      typeof triple.reservationKind !== 'string' ||
      (triple.reservationKind === 'EVENT_HOLD' && eventHoldId && triple.reservationId !== eventHoldId) ||
      (triple.reservationKind === 'PRIMARY_PLACEHOLDER' && placeholderId && triple.reservationId !== placeholderId)
    ) {
      purgeCarrier();
      return null;
    }
    return triple.stripePaymentIntentId;
  }, [entry, purgeCarrier, eventHoldId, placeholderId]);

  const resumeKnownPayment = useCallback(() => {
    const id = intentId ?? readCarrierId();
    if (!id) return false;
    setIntentId(id);
    setPhase('confirming');
    return true;
  }, [intentId, readCarrierId]);

  // The one conflict path: a known payment is read back; none anywhere is the benign terminal.
  const conflict = useCallback(() => {
    if (!resumeKnownPayment()) setPhase('benignTerminal');
  }, [resumeKnownPayment]);

  // The card form's errors the step acts on: card paused or the hold gone re-reads the flow; too close to pay is its own state.
  const onError = useCallback(
    (error: CheckoutError) => {
      if (error.code === 'CARD_UNAVAILABLE') setCardRefused(true);
      // Forms owed after all (added since the page read the booking): the re-read routes to them.
      // No number to call after all (service-by-phone D5): the re-read asks for it.
      if (['CARD_UNAVAILABLE', 'HOLD_EXPIRED', 'FORMS_OUTSTANDING', 'PHONE_NUMBER_REQUIRED'].includes(error.code)) refetchFlow();
      else if (error.code === 'PAYMENT_WINDOW_TOO_CLOSE') setPhase('windowTooClose');
    },
    [refetchFlow],
  );

  const onPaymentSubmitted = useCallback(({ stripePaymentIntentId }: { stripePaymentIntentId: string }) => {
    setIntentId(stripePaymentIntentId);
    setPhase('confirming');
  }, []);

  const pay = useCallback(() => {
    if (timerExpired || submitting || !complete) return;
    setDecline(null);
    void ref.current?.submit();
  }, [timerExpired, submitting, complete]);

  // B8: the slow-confirm timer, from the moment confirming starts (not from the reads).
  useEffect(() => {
    if (phase !== 'confirming') return;
    const timer = setTimeout(() => setPhase('slowConfirm'), SLOW_CONFIRM_MS);
    return () => clearTimeout(timer);
  }, [phase]);

  // The read's endings.
  const { data, error } = status;
  useEffect(() => {
    if (!confirming) return;
    // STALE_CHECKOUT: forget the payment, start the card form over; a fresh attempt supersedes it on the server.
    if (error?.code === 'STALE_CHECKOUT') {
      settleStripeReturn();
      setIntentId(null);
      purgeCarrier();
      setPhase('form');
      setAttempt((n) => n + 1);
      return;
    }
    if (data?.state === 'CONFIRMED') {
      handlers.current.onConfirmedNow?.(data.bookingId);
      setPhase('confirming');
      const timer = setTimeout(() => {
        handlers.current.onBooked(data.bookingId);
        booked.current = true;
      }, SUCCESS_DISPLAY_MS);
      return () => clearTimeout(timer);
    }
    if (data?.state === 'LAPSED') {
      // The hold's own recovery takes over once the flow is read again.
      settleStripeReturn();
      setPhase('form');
      refetchFlow();
    }
  }, [data, error, confirming, purgeCarrier, refetchFlow]);

  const panel = useMemo(
    () => ({
      key: attempt,
      ref,
      entry,
      intentRequest: transport.intentRequest,
      onCompleteChange: setComplete,
      onIntentReady: setIntentExpiresAt,
      onSubmitInFlightChange: setSubmitting,
      onPaymentSubmitted,
      onConflict: conflict,
      onDecline: setDecline,
      onError,
    }),
    [attempt, entry, transport.intentRequest, onPaymentSubmitted, conflict, onError],
  );

  return { phase, confirming, complete, submitting, decline, timerExpired, cardRefused, expiresAt, pay, conflict, resumeKnownPayment, resuming, panel };
}
